import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';

import { ZoneLibraryWorldPreview } from './world-map';
import { ZoneLibraryIllustrationPreview } from './illustration';
import { usePendingZoneById } from '../../hooks/services/use-zones-library';
import { ApproveZoneAsync, RejectZoneAsync } from '../../services/zone';
import { useToast } from '../../contexts/toast-context';
import { appStore } from '../../stores/app-store';
import YesNoDialog from '../../components/dialogs/yes-no-dialog';
import { YesNoDialogResult } from '../../types/dialog-result';

export const ZonePendingPreviewWindow: React.FC<{
  zoneId: string;
  onClose: Function;
}> = ({ zoneId, onClose }) => {
  const { data, isLoading } = usePendingZoneById(zoneId);
  const { onToastOpen } = useToast();
  const [confirmAction, setConfirmAction] = React.useState<
    'Approve' | 'Reject' | null
  >(null);

  const mapType = React.useMemo(() => {
    if (!data?.content) return 'openStreetMap';

    return (data.content.maps ?? []).at(0)?.type ?? 'openStreetMap';
  }, [data]);

  const handleConfirm = async (result: YesNoDialogResult) => {
    if (result === 'yes' && confirmAction) {
      try {
        appStore.showLoading();
        if (confirmAction === 'Approve') {
          await ApproveZoneAsync(parseInt(zoneId));
          onToastOpen({
            title: 'Success',
            body: 'Zone has been approved.',
            style: 'success'
          });
        } else {
          await RejectZoneAsync(parseInt(zoneId));
          onToastOpen({
            title: 'Success',
            body: 'Zone has been rejected.',
            style: 'success'
          });
        }
        onClose();
      } catch (ex) {
        onToastOpen({
          title: 'Error',
          body: `Failed to ${confirmAction.toLowerCase()} zone.`,
          style: 'error'
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setConfirmAction(null);
  };

  return (
    <>
      <Window
        title={'Preview'}
        initialWidth={750}
        initialHeight={650}
        onClose={() => onClose()}
        className={'zone-library'}>
        {isLoading || !data?.content ? (
          <> </>
        ) : mapType !== 'illustrated' ? (
          <ZoneLibraryWorldPreview content={data.content} />
        ) : (
          <ZoneLibraryIllustrationPreview content={data.content} />
        )}

        <WindowActionsBar>
          <Button themeColor={'secondary'} onClick={() => onClose()}>
            Close
          </Button>
          <Button
            themeColor={'error'}
            disabled={isLoading}
            className={'ms-auto me-1'}
            onClick={() => setConfirmAction('Reject')}>
            Reject
          </Button>
          <Button
            themeColor={'success'}
            disabled={isLoading}
            onClick={() => setConfirmAction('Approve')}>
            Approve
          </Button>
        </WindowActionsBar>
      </Window>

      {confirmAction && (
        <YesNoDialog
          title={confirmAction === 'Approve' ? 'Approve zone' : 'Reject zone'}
          onConfirm={handleConfirm}
          onClose={() => setConfirmAction(null)}>
          {confirmAction === 'Approve'
            ? 'Are you sure you want to approve this zone?'
            : 'Are you sure you want to reject this zone?'}
        </YesNoDialog>
      )}
    </>
  );
};
